import { Bot, ChevronDown, ChevronRight, Cog } from 'lucide-react';
import { useState } from 'react';
import { MarkdownContent } from './MarkdownContent';

/**
 * テキストパート（旧形式）
 */
interface TextPart {
  type: 'text';
  text: string;
}

/**
 * システムプロンプトパート（新形式）
 */
interface SystemPromptPart {
  type: 'system-prompt';
  text: string;
  agentType: 'orchestrator' | 'subagent';
  agentId?: string;
  agentName?: string;
  generatedAt: string;
}

/**
 * メッセージパート（システムプロンプト関連）
 */
type SystemMessagePart = TextPart | SystemPromptPart | { type: string; text?: string };

/**
 * メッセージ型
 */
interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content?: string;
  parts?: SystemMessagePart[];
  createdAt: number;
}

/**
 * 抽出されたプロンプト情報
 */
export interface ExtractedPrompt {
  id: string;
  text: string;
  agentType: 'orchestrator' | 'subagent' | 'unknown';
  agentId?: string | undefined;
  agentName?: string | undefined;
  generatedAt?: string | undefined;
}

/**
 * SystemPromptSection Props
 */
interface SystemPromptSectionProps {
  messages: Message[];
}

/**
 * パートが SystemPromptPart かどうかを判定
 */
function isSystemPromptPart(part: SystemMessagePart): part is SystemPromptPart {
  return part.type === 'system-prompt';
}

/**
 * パートが TextPart かどうかを判定
 */
function isTextPart(part: SystemMessagePart): part is TextPart {
  return part.type === 'text' && 'text' in part;
}

/**
 * システムメッセージからプロンプト情報を抽出
 */
export function extractPrompts(messages: Message[]): ExtractedPrompt[] {
  const prompts: ExtractedPrompt[] = [];

  for (const message of messages) {
    if (message.role !== 'system') continue;

    const parts = message.parts ?? [];

    // 新形式: system-prompt パートがある場合
    const systemPromptParts = parts.filter(isSystemPromptPart);
    if (systemPromptParts.length > 0) {
      for (const part of systemPromptParts) {
        prompts.push({
          id: `${message.id}-${part.agentType}-${part.agentId ?? 'main'}`,
          text: part.text,
          agentType: part.agentType,
          agentId: part.agentId,
          agentName: part.agentName,
          generatedAt: part.generatedAt,
        });
      }
      continue;
    }

    // 旧形式: text パートまたは content から取得
    const textParts = parts.filter(isTextPart);
    if (textParts.length > 0) {
      const text = textParts.map((p) => p.text).join('\n');
      prompts.push({
        id: message.id,
        text,
        agentType: 'unknown',
      });
      continue;
    }

    // content フォールバック
    if (message.content) {
      prompts.push({
        id: message.id,
        text: message.content,
        agentType: 'unknown',
      });
    }
  }

  return prompts;
}

/**
 * プロンプトラベルを取得
 */
function getPromptLabel(prompt: ExtractedPrompt): string {
  if (prompt.agentType === 'orchestrator') {
    return 'オーケストレーター';
  }
  if (prompt.agentType === 'subagent') {
    return prompt.agentName ? `サブエージェント: ${prompt.agentName}` : 'サブエージェント';
  }
  return 'システム';
}

/**
 * プロンプトアイコンを取得
 */
function getPromptIcon(agentType: ExtractedPrompt['agentType']) {
  if (agentType === 'subagent') {
    return <Bot className="w-4 h-4" />;
  }
  return <Cog className="w-4 h-4" />;
}

/**
 * 個別プロンプト表示コンポーネント
 */
function PromptItem({ prompt }: { prompt: ExtractedPrompt }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-3 px-6 py-3 hover:bg-muted/50 transition-colors"
      >
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center ${
            prompt.agentType === 'subagent'
              ? 'bg-blue-500/20 text-blue-400'
              : 'bg-orange-500/20 text-orange-400'
          }`}
        >
          {getPromptIcon(prompt.agentType)}
        </div>
        <span className="text-sm text-foreground">{getPromptLabel(prompt)}</span>
        <div className="ml-auto">
          {isOpen ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
      </button>
      {isOpen && (
        <div className="px-6 py-4 bg-muted/30">
          <div className="prose prose-invert prose-sm max-w-none">
            <MarkdownContent content={prompt.text} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * システムプロンプトセクションコンポーネント
 *
 * - オーケストレーターのプロンプトのみ表示（サブエージェントはタスク委譲カード内で表示）
 * - 複数のシステムプロンプトがある場合は個別に折りたたみ表示
 * - 既存の折りたたみ UI パターンを維持
 * - 旧形式（role: 'system' + type: 'text'）との互換性を維持
 */
export function SystemPromptSection({ messages }: SystemPromptSectionProps) {
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);

  // システムメッセージをフィルタリング
  const systemMessages = messages.filter((m) => m.role === 'system');
  if (systemMessages.length === 0) {
    return null;
  }

  // プロンプト情報を抽出（サブエージェント以外のみ）
  // サブエージェントのプロンプトはタスク委譲カード内で表示する
  const allPrompts = extractPrompts(systemMessages);
  const prompts = allPrompts.filter((p) => p.agentType !== 'subagent');
  if (prompts.length === 0) {
    return null;
  }

  // 単一プロンプトの場合はシンプル表示
  const isSinglePrompt = prompts.length === 1;
  const firstPrompt = prompts[0];

  return (
    <div className="rounded-xl border border-orange-500/30 bg-card overflow-hidden">
      <button
        type="button"
        onClick={() => setShowSystemPrompt(!showSystemPrompt)}
        className="flex w-full items-center gap-3 px-6 py-3 bg-muted/50 hover:bg-muted transition-colors"
      >
        <div className="w-8 h-8 bg-orange-500/20 text-orange-400 rounded-full flex items-center justify-center">
          <Cog className="w-4 h-4" />
        </div>
        <span className="text-foreground font-medium">システムプロンプト</span>
        {prompts.length > 1 && (
          <span className="text-xs text-muted-foreground">({prompts.length}件)</span>
        )}
        <div className="ml-auto">
          {showSystemPrompt ? (
            <ChevronDown className="w-4 h-4 text-orange-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-orange-400" />
          )}
        </div>
      </button>

      {showSystemPrompt && (
        <div className="border-t border-orange-500/30">
          {isSinglePrompt && firstPrompt ? (
            // 単一プロンプト: 直接内容を表示
            <div className="px-6 py-4">
              {firstPrompt.agentType !== 'unknown' && (
                <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      firstPrompt.agentType === 'subagent'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-orange-500/20 text-orange-400'
                    }`}
                  >
                    {getPromptIcon(firstPrompt.agentType)}
                  </div>
                  <span>{getPromptLabel(firstPrompt)}</span>
                </div>
              )}
              <div className="prose prose-invert prose-sm max-w-none">
                <MarkdownContent content={firstPrompt.text} />
              </div>
            </div>
          ) : (
            // 複数プロンプト: 個別折りたたみ
            prompts.map((prompt) => <PromptItem key={prompt.id} prompt={prompt} />)
          )}
        </div>
      )}
    </div>
  );
}
